import { useState } from 'react';
import { X, Trophy } from 'lucide-react';
import { quizData } from '@/data/quizData';
import { useHighScores } from '@/react-app/hooks/useHighScores';

interface HighScoresModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function HighScoresModal({ isOpen, onClose }: HighScoresModalProps) {
  const [selectedQuizId, setSelectedQuizId] = useState(quizData[0]?.quizID || '');
  const { highScores, loading, error } = useHighScores(selectedQuizId, 10);

  if (!isOpen) return null;

  const formatTime = (ms: number): string => {
    const seconds = ms / 1000;
    return seconds.toFixed(2);
  };

  const formatDateTime = (date: Date): string => {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8 text-yellow-400" />
            <h2 className="text-3xl font-bold text-white">High Scores</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-auto">
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Select Quiz
            </label>
            <select
              value={selectedQuizId}
              onChange={(e) => setSelectedQuizId(e.target.value)}
              className="w-full md:w-64 px-4 py-2 border-2 border-blue-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {quizData.map((quiz) => (
                <option key={quiz.quizID} value={quiz.quizID}>
                  {quiz.quizName}
                </option>
              ))}
            </select>
          </div>

          {loading && (
            <div className="text-center py-12 text-blue-900">
              Loading high scores...
            </div>
          )}

          {error && (
            <div className="text-center py-12 text-red-600">
              {error}
            </div>
          )}

          {!loading && !error && highScores.length === 0 && (
            <div className="text-center py-12 text-gray-600">
              No high scores yet. Be the first to complete this quiz!
            </div>
          )}

          {!loading && !error && highScores.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-blue-900">
                    <th className="text-left py-3 px-4 font-bold text-blue-900">Rank</th>
                    <th className="text-left py-3 px-4 font-bold text-blue-900">Name</th>
                    <th className="text-left py-3 px-4 font-bold text-blue-900">Score</th>
                    <th className="text-left py-3 px-4 font-bold text-blue-900">Time (s)</th>
                    <th className="text-left py-3 px-4 font-bold text-blue-900">Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {highScores.map((score, index) => (
                    <tr
                      key={score.id}
                      className="border-b border-gray-200 hover:bg-blue-50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {index === 0 && <Trophy className="w-5 h-5 text-yellow-500" />}
                          {index === 1 && <Trophy className="w-5 h-5 text-gray-400" />}
                          {index === 2 && <Trophy className="w-5 h-5 text-orange-600" />}
                          <span className="font-semibold text-blue-900">{index + 1}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900">
                        {score.playerName}
                      </td>
                      <td className="py-3 px-4 text-gray-700">
                        {score.score}/{score.totalQuestions} ({score.accuracy.toFixed(1)}%)
                      </td>
                      <td className="py-3 px-4 text-gray-700">
                        {formatTime(score.timeMs)}
                      </td>
                      <td className="py-3 px-4 text-gray-700">
                        {formatDateTime(score.timestamp)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
